import { useState, useCallback } from 'react';
import { productTypes } from '../../../utils/productHelpers';

/**
 * @typedef {Object} UseAccountActionsReturn
 * @property {string | null} selectedAccountActivity
 * @property {string | null} flippedAccountId
 * @property {'withdraw' | 'withdraw-confirm' | 'deposit' | null} actionType
 * @property {(accountId: string, productType?: string) => void} startWithdrawal
 * @property {(accountId: string) => void} startDeposit
 * @property {() => void} unflipCard
 * @property {(accountId: string) => void} toggleAccountActivity
 * @property {(type: 'withdraw' | 'withdraw-confirm' | 'deposit' | null) => void} setActionType
 */

/**
 * Custom hook to manage account card flip actions (withdrawal/deposit)
 * @returns {UseAccountActionsReturn} Action state and handlers
 */
export function useAccountActions() {
    const [selectedAccountActivity, setSelectedAccountActivity] = useState(/** @type {string | null} */ (null));
    const [flippedAccountId, setFlippedAccountId] = useState(/** @type {string | null} */ (null));
    const [actionType, setActionType] = useState(/** @type {'withdraw' | 'withdraw-confirm' | 'deposit' | null} */ (null));

    const startWithdrawal = useCallback((/** @type {string} */ accountId, /** @type {string | undefined} */ productType) => {
        setActionType(productType === productTypes.InstantAccessCashISA ? 'withdraw-confirm' : 'withdraw');
        setTimeout(() => {
            setFlippedAccountId(accountId);
        }, 1);
    }, []);

    const startDeposit = useCallback((/** @type {string} */ accountId) => {
        setActionType('deposit');
        setTimeout(() => {
            setFlippedAccountId(accountId);
        }, 1);
    }, []);

    /**
     * Unflip the card - only sets flippedAccountId to null
     * The actionType is cleared via onAfterFlip callback in FlipCard
     * to ensure the back content remains visible during the flip animation
     */
    const unflipCard = useCallback(() => {
        setFlippedAccountId(null);
        // Note: actionType is cleared by FlipCard's onAfterFlip callback
        // This ensures the back content stays rendered during the closing animation
    }, []);

    const toggleAccountActivity = useCallback((/** @type {string} */ accountId) => {
        setSelectedAccountActivity(prev => prev === accountId ? null : accountId);
    }, []);

    return {
        selectedAccountActivity,
        flippedAccountId,
        actionType,
        startWithdrawal,
        startDeposit,
        unflipCard,
        toggleAccountActivity,
        setActionType
    };
}
